/**
 * Tenant-aware URL resolution for production deployments where MobiControl
 * Lockdown loads a per-tenant URL (e.g., app.mobilauncher.com/t/acme-corp)
 * and the renderer fetches that tenant's config + license at boot.
 *
 * Conventions supported:
 *   1. Path: `/t/:tenantId[/...]`            (preferred — clean URL for kiosk)
 *   2. Query: `?tenant=:tenantId`            (fallback for environments that
 *                                             can't do path-based routing)
 *   3. None                                  (demo / dev — falls back to the
 *                                             public/ static files generated
 *                                             by predev hooks)
 *
 * URL templates are taken from Vite env vars so a single build artifact
 * can target multiple hosting layouts (Cloudflare Pages static, licensing-
 * server endpoint, customer-hosted CDN, etc.) without code changes:
 *   - VITE_CONFIG_URL_TEMPLATE   e.g. 'https://api.mobilauncher.com/configs/{tenantId}'
 *   - VITE_LICENSE_URL_TEMPLATE  e.g. 'https://api.mobilauncher.com/licenses/by-tenant/{tenantId}'
 * `{tenantId}` is replaced with the URL-encoded tenant id.
 *
 * When no template is set but a tenantId IS present, we resolve against
 * sibling static files: `./configs/<tenantId>.json` / `./licenses/<tenantId>.json`.
 * That convention matches the simplest possible Cloudflare Pages deploy
 * where the admin's Builder export is dropped in alongside the SPA.
 */

// `.trim()` guards against a stray trailing newline/space in the Pages env
// value (easy to introduce when pasting). An untrimmed `…/{tenantId}\n` only
// bites once the tenant id gets URL-encoded somewhere (the browser silently
// strips raw control chars from URLs, so the bug hides until then).
const CONFIG_URL_TEMPLATE: string =
  ((import.meta.env['VITE_CONFIG_URL_TEMPLATE'] as string | undefined) ?? '').trim();
const LICENSE_URL_TEMPLATE: string =
  ((import.meta.env['VITE_LICENSE_URL_TEMPLATE'] as string | undefined) ?? '').trim();
const BROADCASTS_URL_TEMPLATE: string =
  ((import.meta.env['VITE_BROADCASTS_URL_TEMPLATE'] as string | undefined) ?? '').trim();

/**
 * Read the tenant id from the current URL, or `null` for the demo path.
 *
 * Path match is loose by design — `/t/acme-corp/anything/else` resolves
 * to `acme-corp` so deep links from MobiControl Lockdown (which may
 * append fragments or trailing slashes) all converge on the same tenant.
 */
export function resolveTenantId(loc: { pathname: string; search: string } = window.location): string | null {
  const m = loc.pathname.match(/^\/t\/([^/]+)/);
  if (m && m[1]) return decodeURIComponent(m[1]);

  const params = new URLSearchParams(loc.search);
  const q = params.get('tenant');
  if (q && q.length > 0) return q;

  return null;
}

/**
 * Resolve where to fetch the launcher config from. Logic:
 *   - tenant + explicit template → template with {tenantId} substituted
 *   - tenant + prod build        → hosted licensing-server endpoint
 *   - tenant + dev/no template   → ./configs/<tenantId>.json (sibling static)
 *   - no tenant                  → ./config.json (legacy demo / single-tenant)
 */
export function resolveConfigUrl(loc?: { pathname: string; search: string }): string {
  const tenantId = resolveTenantId(loc);
  if (tenantId && CONFIG_URL_TEMPLATE.length > 0) {
    return CONFIG_URL_TEMPLATE.replace('{tenantId}', encodeURIComponent(tenantId));
  }
  if (tenantId && import.meta.env.PROD) {
    return `https://api.mobilauncher.com/configs/${encodeURIComponent(tenantId)}`;
  }
  if (tenantId) {
    return `./configs/${encodeURIComponent(tenantId)}.json`;
  }
  return './config.json';
}

/**
 * Resolve where to fetch the license from. Same shape as resolveConfigUrl.
 * Returns the demo-license path when no tenant is present so dev / demo
 * deployments continue to work unchanged.
 *
 * **Production default is load-bearing.** Without it, a Pages deploy that
 * forgets to set `VITE_LICENSE_URL_TEMPLATE` silently falls back to
 * `./licenses/<tenantId>.json` — but Pages' SPA fallback serves index.html
 * for that path (200 OK, text/html), JSON.parse fails, license becomes
 * null, the engine drops to Core, and every paid feature gate quietly
 * closes. Pinning the prod default to the hosted licensing-server
 * endpoint here makes the launcher work out of the box.
 */
export function resolveLicenseUrl(loc?: { pathname: string; search: string }): string {
  const tenantId = resolveTenantId(loc);
  if (tenantId && LICENSE_URL_TEMPLATE.length > 0) {
    return LICENSE_URL_TEMPLATE.replace('{tenantId}', encodeURIComponent(tenantId));
  }
  if (tenantId && import.meta.env.PROD) {
    return `https://api.mobilauncher.com/licenses/by-tenant/${encodeURIComponent(tenantId)}`;
  }
  if (tenantId) {
    return `./licenses/${encodeURIComponent(tenantId)}.json`;
  }
  return './license.json';
}

/**
 * Resolve where to poll for active broadcasts. Returns null when no
 * tenant is resolvable OR no broadcasts URL can be constructed.
 *
 * Resolution order for the tenant:
 *   1. `tenantIdOverride` argument — used by the Builder preview, which
 *      receives the tenantId via postMessage and isn't loaded under a
 *      `/t/:tenantId` URL path of its own.
 *   2. The current location's `/t/:tenantId` path or `?tenant=` query.
 *
 * URL template:
 *   - If `VITE_BROADCASTS_URL_TEMPLATE` is set at build time, use it.
 *   - Otherwise, default to `https://api.mobilauncher.com/broadcasts/{tenantId}`
 *     in production builds so the preview iframe + auto-built renderer
 *     deployments both work without extra env config.
 *
 * Broadcasts are a network-only feature (no offline source), so the
 * caller skips polling entirely when this returns null.
 */
export function resolveBroadcastsUrl(
  loc?: { pathname: string; search: string },
  tenantIdOverride?: string,
): string | null {
  const tenantId = tenantIdOverride ?? resolveTenantId(loc);
  if (!tenantId) return null;
  const template =
    BROADCASTS_URL_TEMPLATE.length > 0
      ? BROADCASTS_URL_TEMPLATE
      : import.meta.env.PROD
        ? 'https://api.mobilauncher.com/broadcasts/{tenantId}'
        : '';
  if (template.length === 0) return null;
  return template.replace('{tenantId}', encodeURIComponent(tenantId));
}
